import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { createApiCall } from 'services'

/**
 * VRG-565 — which company users manage a location.
 *
 * Separate from `useAddresses` rather than a field on the address payload: the binding
 * is its own resource with its own permission (`address_edit` on write, `address_view`
 * on read — see the routes on `AddressAdminController`), and folding it into the address
 * PUT would mean every address save silently rewrites the manager set, including saves
 * made by a surface that never showed the picker.
 *
 * The response is bare user IDS. The one consumer already loads the company member list
 * it renders the picker from, so shipping names here would be a second projection of the
 * same people, drifting from the first.
 */

// Mirrors `useAddresses`: with VITE_SKIP_AUTH the Address Book is fully exercisable
// against localStorage with no API running, and a field that skips this branch silently
// does nothing in that mode.
const SKIP_AUTH = import.meta.env.VITE_SKIP_AUTH === 'true'
const LOCAL_KEY = 'vera:address-managers'

type LocalMap = Record<string, string[]>

const readLocal = (): LocalMap => {
  try {
    const raw = localStorage.getItem(LOCAL_KEY)
    return raw ? (JSON.parse(raw) as LocalMap) : {}
  } catch {
    return {}
  }
}

const writeLocal = (map: LocalMap) => {
  try {
    localStorage.setItem(LOCAL_KEY, JSON.stringify(map))
  } catch {
    /* storage unavailable — the dev-only path degrades to not persisting */
  }
}

export const addressManagerQueryKeys = {
  forAddress: (addressId: string) => ['company', 'addresses', addressId, 'managers'] as const,
  forCompany: () => ['company', 'addresses', 'managers'] as const,
}

/**
 * VRG-528 — EVERY location's managers, in one request.
 *
 * The Address Book renders a card per address and shows who manages each. Reusing
 * `useAddressManagers` per card would be an N+1 storm — and worse than the request count
 * suggests, since each is its own `useQuery`: N cache entries, N loading states, and
 * cards that resolve one at a time down the page. This is the one bulk read behind it
 * (`GET /addresses/managers`, scoped server-side by the caller's company).
 *
 * Returns a MAP keyed by address id. An address with no managers is ABSENT from the
 * server's response (see the endpoint's DTO) and therefore absent here, so a caller must
 * read it as `map[id] ?? []` — which is also exactly the distinction the page has to
 * render: absent means "nobody manages this location".
 */
export const useCompanyAddressManagers = () => {
  const query = useQuery({
    queryKey: addressManagerQueryKeys.forCompany(),
    queryFn: async (): Promise<Record<string, string[]>> => {
      if (SKIP_AUTH) return readLocal()
      const response = await createApiCall({method: 'GET', url: '/addresses/managers'})
      const items = (response.data?.items ?? []) as {address_id: string; user_ids: string[]}[]
      return Object.fromEntries(items.map(item => [item.address_id, item.user_ids ?? []]))
    },
    staleTime: 1000 * 60,
    refetchOnWindowFocus: false,
  })

  return {
    managersByAddressId: query.data ?? {},
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: query.refetch,
  }
}

export const useAddressManagers = (addressId?: string | null) => {
  const query = useQuery({
    queryKey: addressManagerQueryKeys.forAddress(addressId ?? ''),
    queryFn: async (): Promise<string[]> => {
      if (!addressId) return []
      if (SKIP_AUTH) return readLocal()[addressId] ?? []
      const response = await createApiCall({
        method: 'GET',
        url: `/addresses/${addressId}/managers`,
      })
      return (response.data?.user_ids ?? []) as string[]
    },
    // A create-mode modal has no address yet, so there is nothing to fetch. Without this
    // the hook would fire a request for `/addresses//managers` on every open.
    enabled: Boolean(addressId),
    staleTime: 1000 * 60,
    refetchOnWindowFocus: false,
  })

  return {
    managerUserIds: query.data ?? [],
    isLoading: Boolean(addressId) && query.isLoading,
    isError: query.isError,
    refetch: query.refetch,
  }
}

export const useSetAddressManagers = () => {
  const queryClient = useQueryClient()

  const mutation = useMutation<string[], Error, { addressId: string; userIds: string[] }>({
    mutationFn: async ({ addressId, userIds }) => {
      if (SKIP_AUTH) {
        const map = readLocal()
        map[addressId] = userIds
        writeLocal(map)
        return userIds
      }
      const response = await createApiCall({
        method: 'PUT',
        url: `/addresses/${addressId}/managers`,
        data: { user_ids: userIds },
      })
      return (response.data?.user_ids ?? []) as string[]
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: addressManagerQueryKeys.forAddress(variables.addressId),
      })
      // The Address Book's card list reads the BULK query, so a save that only
      // invalidated the per-address one would update the modal and leave the card behind
      // it stale until a hard reload — which is precisely the state a user would read as
      // "it didn't save".
      queryClient.invalidateQueries({queryKey: addressManagerQueryKeys.forCompany()})
    },
  })

  return {
    setManagers: mutation.mutateAsync,
    isSavingManagers: mutation.isPending,
  }
}
